/**
 * CreditsTab — beta publish entitlement management for one user.
 *
 * Reads the entitlement summary (credits / status / source / published count)
 * and the consumption history from the entitlement endpoints, and exposes
 * Grant / Revoke actions. All credit math lives in the backend provider; this
 * tab only reads the view model + triggers mutations, so the future
 * subscription provider needs no change here.
 */
import { useState } from 'react';
import { useUserEntitlement, useUserPublishHistory } from '../hooks';
import { TableSkeleton } from '../components/LoadingSkeleton';
import { ErrorState } from '../components/EmptyState';
import { PublishHistoryTable } from '../components/PublishHistoryTable';
import { GrantRevokeCreditsModal } from '../components/GrantRevokeCreditsModal';
import type { EntitlementSource } from '../types';

const SOURCE_LABEL: Record<EntitlementSource, string> = {
  BETA_CREDIT: 'Beta Credit',
  ADMIN_GRANT: 'Beta Credit',
  SUBSCRIPTION: 'Subscription',
  ENTERPRISE: 'Enterprise',
};

export function CreditsTab({ userId }: { userId: string }) {
  const ent = useUserEntitlement(userId);
  const history = useUserPublishHistory(userId);
  const [modal, setModal] = useState<'grant' | 'revoke' | null>(null);

  if (ent.isLoading) return <TableSkeleton rows={3} cols={2} />;
  if (ent.isError || !ent.data)
    return (
      <ErrorState
        message={(ent.error as Error)?.message ?? 'Failed to load entitlement'}
        onRetry={() => void ent.refetch()}
      />
    );

  const e = ent.data;

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Field label="Remaining credits" value={String(e.remainingCredits)} />
        <Field
          label="Publish rights"
          value={e.canPublish ? 'Can Publish' : 'No Credits'}
          tone={e.canPublish ? 'ok' : 'warn'}
        />
        <Field label="Source" value={SOURCE_LABEL[e.entitlementSource] ?? 'Beta Credit'} />
        <Field label="Published" value={`${e.totalPublished} websites`} />
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setModal('grant')}
          className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest text-white bg-electric-violet hover:bg-electric-violet/90"
        >
          Grant credits
        </button>
        <button
          type="button"
          onClick={() => setModal('revoke')}
          disabled={e.remainingCredits === 0}
          className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-red-400/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
        >
          Revoke credits
        </button>
      </div>

      {/* History */}
      <div className="space-y-2">
        <h3 className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
          Publish history
        </h3>
        {history.isLoading ? (
          <TableSkeleton rows={3} cols={3} />
        ) : history.isError ? (
          <ErrorState
            message={(history.error as Error)?.message ?? 'Failed to load history'}
            onRetry={() => void history.refetch()}
          />
        ) : (
          <PublishHistoryTable rows={history.data ?? []} />
        )}
      </div>

      <GrantRevokeCreditsModal
        open={modal !== null}
        mode={modal ?? 'grant'}
        userId={userId}
        currentCredits={e.remainingCredits}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

function Field({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn';
}) {
  const valueClass =
    tone === 'ok' ? 'text-emerald-300' : tone === 'warn' ? 'text-amber-300' : 'text-on-surface';
  return (
    <div className="glass-panel rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
        {label}
      </div>
      <div className={`text-sm font-bold ${valueClass}`}>{value}</div>
    </div>
  );
}
